import { filter, map } from 'rxjs/operators'
import { getSharedWebSocketStream } from './sharedWebSocketManager'

import { WS_URL } from '@/lib/config'

/**
 * Helper to safely convert dates to timestamps
 */
export function toTimestamp(value: any): number | null {
  if (!value) return null
  if (typeof value === 'number') return value
  const timestamp = new Date(value).getTime()
  return isNaN(timestamp) ? null : timestamp
}

/**
 * Add WebSocket streaming to ANY collection's replication
 *
 * Usage in your replication file:
 *
 * const { stream$, close } = addWebSocketToReplication('collectionName', (doc) => ({
 *   id: doc.id,
 *   name: doc.name,
 *   ...transform other fields...
 * }))
 *
 * Then in replicateRxCollection:
 * pull: {
 *   async handler(...) { ... },
 *   batchSize: 100,
 *   stream$: stream$ as any  // <-- Add this
 * }
 *
 * And cleanup:
 * replicationState.canceled$.subscribe(() => close())
 */
export function addWebSocketToReplication<T>(
  collectionName: string,
  documentTransformer: (doc: any) => T,
  options?: { documentFilter?: (doc: any) => boolean }
): { stream$: any; close: () => void } {

  // Get shared WebSocket (one connection for ALL collections)
  const { stream$: rawStream$, close } = getSharedWebSocketStream(collectionName, WS_URL)

  const documentFilter = options?.documentFilter

  // Transform for RxDB - must be a proper Observable
  const transformedStream$ = rawStream$.pipe(
    map((msg) => {
      // Pass RESYNC through
      if (msg === 'RESYNC') {
        return 'RESYNC'
      }

      // Handle document changes (already filtered by collection)
      if (msg && typeof msg === 'object' && msg.type === 'document_changed' && msg.document) {
        // Optional client-side filter (e.g. drop other users' rows for per-user
        // collections that are broadcast workspace-wide).
        if (documentFilter && !documentFilter(msg.document)) {
          return null
        }

        const transformed = documentTransformer(msg.document)

        return {
          documents: [transformed],
          checkpoint: { updatedAt: msg.timestamp },
        }
      }

      if (msg && typeof msg === 'object' && msg.type === 'document_deleted') {
        return 'RESYNC'
      }

      return null
    }),
    filter((x) => x !== null)
  )

  // Return the Observable stream
  return {
    stream$: transformedStream$,
    close,
  }
}
